import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { api, getToken, openDownload } from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Download, Loader2, CheckCircle2, XCircle, Trash2, FileSpreadsheet, Clock } from "lucide-react";

const ExportsContext = createContext(null);
export const useExports = () => useContext(ExportsContext);

const fmt = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
const humanSize = (n) => (n ? (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`) : "");

export function ExportsProvider({ children }) {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!getToken()) return;
    try { const { data } = await api.get("/exports"); setJobs(data); } catch { /* non-admin o errore: ignora */ }
  }, []);

  const activeCount = jobs.filter((j) => j.status === "queued" || j.status === "processing").length;

  useEffect(() => {
    if (activeCount > 0 && !pollRef.current) {
      pollRef.current = setInterval(refresh, 2500);
    } else if (activeCount === 0 && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => {};
  }, [activeCount, refresh]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startExport = useCallback(async (kind, params = {}, label) => {
    try {
      await api.post("/exports", { kind, params, label });
      toast.success("Export avviato. Lo trovi in \u201cI miei export\u201d.");
      setOpen(true);
      refresh();
    } catch { toast.error("Impossibile avviare l'export"); }
  }, [refresh]);

  const openDrawer = useCallback(() => { setOpen(true); refresh(); }, [refresh]);
  const download = (job) => openDownload(`/exports/${job.id}/download`);
  const remove = async (job) => { try { await api.delete(`/exports/${job.id}`); refresh(); } catch { toast.error("Errore eliminazione"); } };

  return (
    <ExportsContext.Provider value={{ jobs, startExport, openDrawer, activeCount, refresh }}>
      {children}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col" data-testid="exports-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-primary" /> I miei export</SheetTitle>
          </SheetHeader>
          <div className="mt-4 flex-1 overflow-y-auto space-y-2 pr-1">
            {jobs.length === 0 && (
              <p className="text-sm text-muted-foreground py-8 text-center" data-testid="exports-empty">Nessun export. Avvia un export da una qualsiasi pagina con il pulsante “Esporta”.</p>
            )}
            {jobs.map((j) => (
              <div key={j.id} data-testid={`export-job-${j.id}`} className="rounded-lg border border-border p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{j.label || j.kind}</p>
                    <p className="text-[11px] text-muted-foreground">{fmt(j.created_at)}{j.size ? ` \u00b7 ${humanSize(j.size)}` : ""}</p>
                  </div>
                  <div className="shrink-0">
                    {j.status === "queued" && <span className="inline-flex items-center gap-1 text-[11px] text-amber-600"><Clock className="h-3.5 w-3.5" />In coda</span>}
                    {j.status === "processing" && <span className="inline-flex items-center gap-1 text-[11px] text-sky-600"><Loader2 className="h-3.5 w-3.5 animate-spin" />In corso</span>}
                    {j.status === "done" && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" />Pronto</span>}
                    {j.status === "failed" && <span className="inline-flex items-center gap-1 text-[11px] text-rose-600"><XCircle className="h-3.5 w-3.5" />Errore</span>}
                  </div>
                </div>
                {j.status === "failed" && j.error && <p className="text-[11px] text-rose-600 mt-1 break-words">{j.error}</p>}
                <div className="flex items-center gap-2 mt-2">
                  {j.status === "done" && (
                    <Button size="sm" variant="outline" data-testid={`export-download-${j.id}`} onClick={() => download(j)}>
                      <Download className="h-3.5 w-3.5 mr-1.5" />Scarica
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-destructive" data-testid={`export-delete-${j.id}`} onClick={() => remove(j)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </ExportsContext.Provider>
  );
}
